import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';

import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import mappers from '~/components/DebugModal/mappers';
import { TraverseObject } from '~/components/DebugModal/utils';

import { PropertyDebugResponse, getPropertyDebug, refreshPropertyDebug } from '~/services/AccommodationService';
import { diffSeconds, getTimeDifference } from '~/services/TimeService';

// 5 mins
const TTL_IN_SECONDS = 5 * 60;

export default function AccommodationPropertyDebugModal() {
  const [open, setOpen] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [debugData, setDebugData] = useState<PropertyDebugResponse>();
  const [lastRetrievedAt, setLastRetrievedAt] = useState<Date | null>();
  const { propertyDetails } = useAccommodationPropertyDetailsContext();

  const getPropertyDebugFetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setDebugData(undefined);
      setLastRetrievedAt(null);
      const data = await getPropertyDebug({ propertyId: propertyDetails.id });
      setDebugData(data.result);
      setLastRetrievedAt(new Date());
    } catch (error) {
      enqueueSnackbar(`Failed to get property debug data: ${error.message}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, propertyDetails.id]);

  const openDebugModal = useCallback(() => {
    setOpen(true);
    if (!debugData || diffSeconds(lastRetrievedAt, new Date()) > TTL_IN_SECONDS) {
      getPropertyDebugFetch();
    }
  }, [getPropertyDebugFetch, debugData, lastRetrievedAt]);

  const closeDebugModal = useCallback(() => {
    setOpen(false);
  }, []);

  const importantMap = useMemo(() => {
    return (mappers['generic'] ?? (() => null))(debugData);
  }, [debugData]);

  const refreshPropertyDebugFetch = useCallback(
    () => refreshPropertyDebug({ propertyId: propertyDetails.id }),
    [propertyDetails.id],
  );

  const handleRegenerate = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      setIsRegenerating(true);
      try {
        const newData = await refreshPropertyDebugFetch();
        setDebugData(newData.result);
      } catch (error) {
        enqueueSnackbar(`Refresh failed: ${error.message}`, { variant: 'error' });
      }
      enqueueSnackbar('Property debug data refreshed successfully', { variant: 'success' });
      setIsRegenerating(false);
    },
    [refreshPropertyDebugFetch, setIsRegenerating, enqueueSnackbar],
  );

  const handleRefresh = useCallback(() => {
    getPropertyDebugFetch();
  }, [getPropertyDebugFetch]);

  const lastRetrievedAtText = useCallback(() => {
    if (!lastRetrievedAt) {
      return '';
    }
    const diff = getTimeDifference(lastRetrievedAt, new Date(), true, true);
    if (diff) {
      return `${diff} ago`;
    }
    return 'Just now';
  }, [lastRetrievedAt]);

  return (
    <>
      <Button onClick={openDebugModal} title="Open debug modal" variant="contained" size="small">
        Debug Modal
      </Button>

      <Dialog open={open} maxWidth="lg" onClose={closeDebugModal}>
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="h6">Property Debug</Typography>
          {lastRetrievedAt && (
            <Stack direction="row" gap={2} alignItems="center" sx={{ flex: 1, justifyContent: 'flex-end' }}>
              <Typography>Last Retrieved: {lastRetrievedAtText()}</Typography>
              <Button variant="outlined" onClick={handleRefresh}>
                <RefreshIcon />
              </Button>
            </Stack>
          )}
        </Stack>

        <DialogContent sx={{ minWidth: '50vw' }}>
          {(isLoading || isRegenerating) && <CircularProgress />}
          {importantMap !== null && <TraverseObject obj={importantMap} />}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
                enqueueSnackbar('JSON copied to clipboard', { variant: 'success' });
              }}
            >
              Copy JSON to Clipboard
            </Button>
            <Button
              variant="outlined"
              onClick={handleRegenerate}
              disabled={isRegenerating}
              startIcon={isRegenerating ? <CircularProgress size={16} /> : undefined}
            >
              {isRegenerating ? 'Regenerating...' : 'Regenerate state and cache'}
            </Button>
          </Stack>
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              Close
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
